/*
* Resideo/LifeWhere
* Copyright (C) 2018-2023 Resideo/LifeWhere
* mailto:nathan.williams@resideo.com
*/

import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { PageView, Event } from "../GoogleAnalytics";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Snackbar from '@mui/material/Snackbar';
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import alerttypemessagingService from '../../services/alerttypemessaging.service';
import {AssetTypeDict, jsCustomAlert, jsCustomAlertMessaging} from "../../componentObjects";
import Loader from "../Loader/Loader";
import assettypeService from "../../services/assettype.service";
import AlertsService from "../../services/alerts.service";

const NONE_STR = "None";
const initAlertType = {
    name: "",
    description: "",
    ovverideTypeId: 0,
    method: "",
    caseTypeId: null,
    numThreads: null,
    isTimeRange: false,
    isEmailed: false,
    isCase: false,
    manualIntervention: null,
    catastrophicId: null,
    priorityId: 3,
    alertGroupId: null
};

const initAlertMessaging = {
    typeName: "",
    alertMessage: "",
    possibleCauses: "",
    techAction: "",
    homeownerAction: "",
    risk: "",
    severity: "",
    assetTypeId: 6,
    notificationTime: ""
};

function CreateAlertType(props) {

    const {
        classes,
        isLoading,
        currentPartnerId,
        _isMounted,
        setActivePath,
        showUnsavedModal,
        onContinueFromComponent
    } = props;
    const [isMounted, setIsMounted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showUnsaved, setShowUnsaved] = useState(null);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [validated, setValidated] = useState(false);
    
    const [newAlertType, setNewAlertType] = useState(initAlertType);
    const [newAlertTypeMessage, setNewAlertTypeMessage] = useState(initAlertMessaging);

    const updateLocalStorage = (unsavedChanges, typeData, messageData) => {
        localStorage.setItem('unsavedChanges', unsavedChanges);
        localStorage.setItem('newAlertType', JSON.stringify(typeData));
        localStorage.setItem('newAlertTypeMessage', JSON.stringify(messageData));
        
        if (isMounted) {
            setShowSnackbar(false);
        }
    }
    
    /* Input Functions */
    
    const onChangeName = (e) => {
        const newData = { ...newAlertType, name: e.target.value };
        const newMessageData = { ...newAlertTypeMessage, typeName: e.target.value };
        updateLocalStorage(true, newData, newMessageData);
        setNewAlertType(newData);
        setNewAlertTypeMessage(newMessageData);
    }
    
    const onChangeDescription = (e) => {
        const newData = { ...newAlertType, description: e.target.value };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangePriority = (e) => {
        const newData = { ...newAlertType, priorityId: e.target.value };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeMethod = (e) => {
        const val = e.target.value;
        const newData = { ...newAlertType, method: val === NONE_STR ? null : val };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeIsOveriddenBy = (e) => {
        const newData = { ...newAlertType, ovverideTypeId: e.target.value };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeCaseTypeId = (e) => {
        const val = e.target.value;
        const newData = { ...newAlertType, caseTypeId: val === NONE_STR ? null : val };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }

    const onChangeIsCase = (e) => {
        const val = parseInt(e.target.value);
        const newData = { ...newAlertType, isCase: val !== 0 };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }

    const onChangeIsTimeRange = (e) => {
        const val = parseInt(e.target.value);
        const newData = { ...newAlertType, isTimeRange: val !== 0 };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeNumThreads = (e) => {
        const newData = { ...newAlertType, numThreads: parseInt(e.target.value) };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeIsEmailed = (e) => {
        const val = parseInt(e.target.value);
        const newData = { ...newAlertType, isEmailed: val !== 0 };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeManualIntervention = (e) => {
        const val = parseInt(e.target.value);
        const newData = { ...newAlertType, manualIntervention: val !== 0 };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeCatastrophicId = (e) => {
        const newData = { ...newAlertType, catastrophicId: e.target.value };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    const onChangeAlertGroup = (e) => {
        const val = e.target.value;
        const newData = { ...newAlertType, alertGroupId: val === NONE_STR ? null : parseInt(e.target.value) };
        updateLocalStorage(true, newData, newAlertTypeMessage);
        setNewAlertType(newData);
    }
    
    /* Alert Type Messaging Inputs */
    
    const onChangeAlertMessage = (e) => {
        const newData = { ...newAlertTypeMessage, alertMessage: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangePossibleCauses = (e) => {
        const newData = { ...newAlertTypeMessage, possibleCauses: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangeTechAction = (e) => {
        const newData = { ...newAlertTypeMessage, techAction: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangeHomeownerAction = (e) => {
        const newData = { ...newAlertTypeMessage, homeownerAction: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangeRisk = (e) => {
        const newData = { ...newAlertTypeMessage, risk: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangeSeverity = (e) => {
        const newData = { ...newAlertTypeMessage, severity: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangeAssetType = (e) => {
        const newData = { ...newAlertTypeMessage, assetTypeId: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    const onChangeNotificationTime = (e) => {
        // always null?
        const newData = { ...newAlertTypeMessage, notificationTime: e.target.value };
        updateLocalStorage(true, newAlertType, newData);
        setNewAlertTypeMessage(newData);
    }
    
    /* Validate and Save */
    
    const setShowModal = () => {
        setShowSaveConfirm(!showSaveConfirm);
        setLoading(false);
    }
    
    const onSaveType = (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        
        if (form.checkValidity() === false) {
            setValidated(true);
        }
        else {
            setValidated(true);
            setLoading(true);
            setShowSaveConfirm(true);
        }
    }
    
    const saveType = async () => {
        let createObj = {
            alertType: newAlertType,
            alertTypeMessage: newAlertTypeMessage
        };
        await AlertsService.createalerttype(createObj)
            .then(response => {
                if (response !== null && response !== undefined && response !== false) {
                    setStatusMessage("Successfully created alert type");
                    setNewAlertType(initAlertType);
                    setNewAlertTypeMessage(initAlertMessaging);
                    updateLocalStorage(false, initAlertType, initAlertMessaging);
                }
                else {
                    setStatusMessage("Failed to create alert type.");
                }
            })
            .catch(e => {
                console.log(e);
            });
        
        setValidated(false);
        setLoading(false);
        setShowSaveConfirm(false);
        setShowSnackbar(true);

        setTimeout(() => {
            setStatusMessage("");
            setShowSnackbar(false);
        }, 5000);
    }
    
    let contents = loading
        ? <Loader />
        : <div>
            <div className={"form-wrapper"}>
                <Form noValidate validated={validated} onSubmit={onSaveType}>
                    <Row>
                        <Form.Group as={Col} md="4" controlId="typeName">
                            <Form.Label>Alert Type Name</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertType.name} onChange={onChangeName} />
                            <Form.Control.Feedback type="invalid">Alert Type Name is required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertType.description} onChange={onChangeDescription} />
                            <Form.Control.Feedback type="invalid">Description is required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="2" controlId="priority">
                            <Form.Label>Priority</Form.Label>
                            <Form.Control required as="select" value={newAlertType.priorityId} onChange={onChangePriority}>
                                <option value="" disabled defaultValue hidden>Select Priority...</option>
                                <option value={1}>High</option>
                                <option value={2}>Medium</option>
                                <option value={3}>Low</option>
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">Priority is required</Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col} md="3" controlId="method">
                            <Form.Label>Method</Form.Label>
                            <Form.Control required as="select" value={newAlertType.method} onChange={onChangeMethod}>
                                <option value="" disabled defaultValue hidden>Select Method...</option>
                                <option value={"thread"}>Thread</option>
                                <option value={"model"}>Model</option>
                                <option value={null}>None</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="3" controlId="overiddenBy">
                            <Form.Label>Overidden By</Form.Label>
                            <Form.Control required as="select" value={newAlertType.ovverideTypeId} onChange={onChangeIsOveriddenBy}>
                                <option value="" disabled defaultValue hidden>Select Overide...</option>
                                <option value={0}>None</option>
                                <option value={1}>Any State</option>
                                <option value={2}>Higher Priority</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="3" controlId="caseTypeId">
                            <Form.Label>Case Type Id</Form.Label>
                            <Form.Control required as="select" value={newAlertType.caseTypeId} onChange={onChangeCaseTypeId}>
                                <option value="" disabled defaultValue hidden>Select Case Type Id...</option>
                                <option value={null}>None</option>
                                <option value={1}>General</option>
                                <option value={2}>Asset</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="3" controlId="alertGroup">
                            <Form.Label>Alert Group</Form.Label>
                            <Form.Control required as="select" value={newAlertType.alertGroupId} onChange={onChangeAlertGroup}>
                                <option value="" disabled defaultValue hidden>Select Alert Group...</option>
                                <option value={null}>None</option>
                                <option value={1}>Analytics</option>
                                <option value={2}>Device</option>
                                <option value={3}>Equipment</option>
                            </Form.Control>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col} md="3" controlId="isTimeRange">
                            <Form.Label>Is Time Range</Form.Label>
                            <Form.Control required as="select" value={newAlertType.isTimeRange} onChange={onChangeIsTimeRange}>
                                <option value="" disabled defaultValue hidden>Select isTimeRange...</option>
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="3" controlId="isEmailed">
                            <Form.Label>Is Emailed</Form.Label>
                            <Form.Control required as="select" value={newAlertType.isEmailed} onChange={onChangeIsEmailed}>
                                <option value="" disabled defaultValue hidden>Select IsEmailed...</option>
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="3" controlId="isCase">
                            <Form.Label>Is Case</Form.Label>
                            <Form.Control required as="select" value={newAlertType.isCase} onChange={onChangeIsCase}>
                                <option value="" disabled defaultValue hidden>Select IsCase...</option>
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="3" controlId="manualIntervention">
                            <Form.Label>Manual Intervention</Form.Label>
                            <Form.Control required as="select" value={newAlertType.manualIntervention} onChange={onChangeManualIntervention}>
                                <option value="" disabled defaultValue hidden>Select Manual Intervention...</option>
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                            </Form.Control>
                        </Form.Group>
                    </Row>

                    <Row className={"border-bottom"}>
                        <Form.Group as={Col} md="3" controlId="numThreads">
                            <Form.Label>Num Threads</Form.Label>
                            <Form.Control type="text" placeholder="" value={newAlertType.numThreads} onChange={onChangeNumThreads} />
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group as={Col} md="6" controlId="alertMessage">
                            <Form.Label>Message</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertTypeMessage.alertMessage} onChange={onChangeAlertMessage} />
                            <Form.Control.Feedback type="invalid">Alert Message is required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="2" controlId="severity">
                            <Form.Label>Severity</Form.Label>
                            <Form.Control required as="select" value={newAlertTypeMessage.severity} onChange={onChangeSeverity}>
                                <option value="" disabled defaultValue hidden>Select Severity...</option>
                                <option value={"Critical"}>Critical</option>
                                <option value={"Warning"}>Warning</option>
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">Severity is required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="2" controlId="assetType">
                            <Form.Label>Asset Type</Form.Label>
                            <Form.Control required as="select" value={newAlertTypeMessage.assetTypeId} onChange={onChangeAssetType}>
                                <option value="" disabled defaultValue hidden>Select Asset Type...</option>
                                <option value={0}>Other</option>
                                <option value={1}>Furnace</option>
                                <option value={2}>Air Conditioner</option>
                                <option value={3}>Heat Pump</option>
                                <option value={4}>Air Handler</option>
                                <option value={5}>General</option>
                                <option value={6}>All</option>
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">Asset Type is required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="2" controlId="notificationTime">
                            <Form.Label>Notification Time</Form.Label>
                            <Form.Control required as="select" value={newAlertTypeMessage.notificationTime} onChange={onChangeNotificationTime}>
                                <option value="" disabled defaultValue hidden>Select Notification Time...</option>
                                <option value={"24"}>24hr after pro</option>
                                <option value={"48"}>48hr after pro</option>
                                <option value={null}>None</option>
                            </Form.Control>
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} md="6" controlId="possibleCauses">
                            <Form.Label>Possible Causes</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertTypeMessage.possibleCauses} onChange={onChangePossibleCauses} />
                            <Form.Control.Feedback type="invalid">Possible Causes required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="risk">
                            <Form.Label>Risk</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertTypeMessage.risk} onChange={onChangeRisk} />
                            <Form.Control.Feedback type="invalid">Risk is required</Form.Control.Feedback>
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} md="6" controlId="techAction">
                            <Form.Label>Technician Action</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertTypeMessage.techAction} onChange={onChangeTechAction} />
                            <Form.Control.Feedback type="invalid">Technician Action is required</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="homeownerAction">
                            <Form.Label>Homeowner Action</Form.Label>
                            <Form.Control required type="text" placeholder="" value={newAlertTypeMessage.homeownerAction} onChange={onChangeHomeownerAction} />
                            <Form.Control.Feedback type="invalid">Homeowner Action is required</Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Button type="submit" className={"form-save-btn"}>Save</Button>
                </Form>
            </div>
        </div>;

    return (
        <div>
            <div className='infoPage'>
                <h2 className="pageTitle dashboard-title" id="tabelLabel">
                    Create Alert Type
                </h2>
                {contents}

                <ConfirmationModal
                    show={showSaveConfirm}
                    onYes={saveType}
                    onClick={setShowModal}
                    message={"Would you like to create this Alert Type?"}
                    title={"Create Alert Type"}></ConfirmationModal>

                <Snackbar open={showSnackbar} message={statusMessage} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} autoHideDuration={6000} />
            </div>
        </div>
    );
}

export default CreateAlertType;